import Vue from 'vue';
import axios from 'axios';
import DOMPurify from 'dompurify';
import translations from './minaSidorTranslations';
import homeUrl from './helpers/homeUrl';
import bookingUrl from './helpers/bookingUrl';

var PURIFY_OPTS = { ADD_ATTR: ['target', 'rel'] };

function linkify(text) {
  return text.replace(
    /(?:https?:\/\/|www\.)[^\s<>"')\]]+/gi,
    function (url) {
      var href = url.startsWith('http') ? url : 'https://' + url;
      return '<a href="' + href + '" target="_blank" rel="noopener noreferrer">' + url + '</a>';
    }
  );
}

Vue.directive('click-outside', {
  bind: function (el, binding) {
    el._clickOutsideHandler = function (e) {
      if (!el.contains(e.target)) {
        binding.value();
      }
    };
    document.addEventListener('click', el._clickOutsideHandler);
  },
  unbind: function (el) {
    document.removeEventListener('click', el._clickOutsideHandler);
    delete el._clickOutsideHandler;
  },
});

jQuery(document).ready(function () {
  if (!document.getElementById('mina-sidor-app')) {
    return;
  }

  new Vue({
    el: '#mina-sidor-app',
    data: {
      lang: localStorage.getItem('nk_lang') || 'sv',
      cbPatientId: localStorage.getItem('nk_cb_patient_id') || '',
      manualPatientId: '',
      activeTab: 'bokningar',
      userDropdownOpen: false,
      loading: true,
      upcomingBookings: [],
      pastBookings: [],
      contactEmail: '',
      emailSaved: false,
      editingEmail: false,
      savedEmailBackup: '',
      conversations: [],
      activeConversation: null,
      showNewContactForm: true,
      messageSubject: '',
      messageType: '',
      messageSubType: '',
      messageBody: '',
      attachedFiles: [],
      expandedMessages: {},
      autoOpenedLatest: false,
      conversationPollTimer: null,
      showReplyForm: false,
      replyBody: '',
      replyFiles: [],
      seenMessageCounts: {},
      typeGroups: [],
      sendingMessage: false,
      sendingReply: false,
      messageSentSuccess: false,
      conversationsPage: 1,
      conversationsPerPage: 5,
      selectedLocation: '',
      adminTreatmentDesc: '',
      adminDoctorName: '',
      adminTreatmentDate: '',
      adminComment: '',
      adminDelivery: '',
      adminRequisitionAccepted: false,
      adminAddressName: '',
      adminAddressStreet: '',
      adminAddressZip: '',
      adminAddressCity: '',
      sendingUploadText: 'Ditt ärende skapas...',
      _sendingTimer: null,
      autoReplyText: '',
      showAutoReplyPopup: false,
      newMessageInThread: false,
      rateLimitMessage: '',
      rateLimitTimer: null,
      uploadErrorMessage: '',
      cancellingBookingId: null,
      showCancelModal: false,
      cancelTarget: null,
      typeDropdownOpen: false,
      subTypeDropdownOpen: false,
      locationDropdownOpen: false,
    },
    created() {
      localStorage.removeItem('nk_bankid_login_pending');
      localStorage.removeItem('nk_bankid_order_ref');
      document.cookie = 'nk_bankid_pending=;path=/;max-age=0';

      this.fetchBookings();
      this.fetchConversationTypes();
      this.fetchAutoReply();
      this.fetchCbPatientId();

      try {
        var saved = localStorage.getItem('nk_seen_message_counts');
        if (saved) {
          this.seenMessageCounts = JSON.parse(saved);
        }
      } catch (e) {}

      var savedEmail = localStorage.getItem('nk_contact_email');
      if (savedEmail) {
        this.contactEmail = savedEmail;
        this.emailSaved = true;
        var self = this;
        this.fetchConversations().then(function () {
          if (self.conversations.length > 0 && self.upcomingBookings.length === 0) {
            self.activeTab = 'arenden';
          }
        });
        this.startPolling();
      }

      document.addEventListener('click', this.handleOutsideClick);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.userDropdownOpen = false;
        }
      });
    },
    computed: {
      t() {
        return translations[this.lang] || translations.sv;
      },
      paginatedConversations() {
        var start = (this.conversationsPage - 1) * this.conversationsPerPage;
        return this.conversations.slice(start, start + this.conversationsPerPage);
      },
      conversationsTotalPages() {
        return Math.ceil(this.conversations.length / this.conversationsPerPage) || 1;
      },
      totalUnreadCount() {
        return this.conversations.reduce(function (sum, c) { return sum + (c.unreadCount || 0); }, 0);
      },
      selectedGroup() {
        var self = this;
        if (!this.messageType) return null;
        return this.typeGroups.find(function (g) { return g.key === self.messageType; }) || null;
      },
      availableSubTypes() {
        if (!this.selectedGroup) return [];
        return this.selectedGroup.types || [];
      },
      showSubTypeDropdown() {
        return this.selectedGroup && this.selectedGroup.types.length > 1;
      },
      resolvedTypeKey() {
        if (!this.selectedGroup) return '';
        if (this.selectedGroup.types.length === 1) return this.selectedGroup.types[0].key;
        return this.messageSubType;
      },
      isAdminType() {
        return this.messageType === 'administration';
      },
      resolvedSubTypeLabel() {
        if (!this.selectedGroup) return '';
        if (this.selectedGroup.types.length === 1) return (this.selectedGroup.types[0].label || '').toLowerCase();
        var self = this;
        var st = this.selectedGroup.types.find(function (t) { return t.key === self.messageSubType; });
        return st ? (st.label || '').toLowerCase() : '';
      },
      isAdminRequisition() {
        if (!this.isAdminType) return false;
        var label = this.resolvedSubTypeLabel;
        return label.indexOf('rekvisition') !== -1 || label.indexOf('requisition') !== -1;
      },
      isJournalRequisition() {
        if (!this.isAdminRequisition) return false;
        var label = this.resolvedSubTypeLabel;
        return label.indexOf('journal') !== -1;
      },
      isPhotoRequisition() {
        if (!this.isAdminRequisition) return false;
        var label = this.resolvedSubTypeLabel;
        return label.indexOf('foto') !== -1 || label.indexOf('photo') !== -1;
      },
      isAdminJournal() {
        return this.isJournalRequisition;
      },
      isAdminPhoto() {
        return this.isPhotoRequisition;
      },
      hasAdminFields() {
        return this.isAdminJournal || this.isAdminPhoto || this.isAdminRequisition;
      },
      typeFullySelected() {
        if (!this.messageType) return false;
        if (this.showSubTypeDropdown && !this.messageSubType) return false;
        return true;
      },
      subTypeDisplayLabel() {
        if (!this.messageSubType || !this.selectedGroup) return '';
        var sub = this.messageSubType;
        var found = this.selectedGroup.types.find(function (t) { return t.key === sub; });
        return found ? found.label : '';
      },
      typeDisplayLabel() {
        if (!this.selectedGroup) return '';
        return this.selectedGroup.label;
      },
    },
    watch: {
      emailSaved(val) {
        if (val) {
          this.startPolling();
        }
      },
      activeTab(val) {
        if (val === 'arenden') {
          this.openLatestConversation();
        }
      },
    },
    beforeDestroy() {
      document.removeEventListener('click', this.handleOutsideClick);
      this.stopPolling();
    },
    methods: {
      setLang(lang) {
        this.lang = lang;
        try { localStorage.setItem('nk_lang', lang); } catch (e) {}
      },
      handleOutsideClick(e) {
        if (this.userDropdownOpen && !e.target.closest('.ms-header-right')) {
          this.userDropdownOpen = false;
        }
      },

      fetchBookings() {
        this.loading = true;
        var self = this;

        if (window.location.search.indexOf('demo=1') !== -1) {
          var nowTs = Math.floor(Date.now() / 1000);
          self.upcomingBookings = [
            { id: 'demo-1', treatment: 'Konsultation Bröstförstoring', city: 'Stockholm', provider: 'Marcus Ehrström Plastikkirurg', dateFormatted: 'Imorgon, 13:00', dateTimestamp: nowTs + 12 * 3600, status: 'confirmed', isOperation: false, priceFormatted: '', receiptUrl: null },
            { id: 'demo-2', treatment: 'Botox', city: 'Stockholm', provider: 'Sandra Sellström Injektionssjuksköterska', dateFormatted: '11 jun, 11:00', dateTimestamp: nowTs + 7 * 86400, status: 'confirmed', isOperation: false, priceFormatted: '', receiptUrl: null },
            { id: 'demo-3', treatment: 'Konsultation Bukplastik', city: 'Göteborg', provider: 'Mattias Lidén Plastikkirurg', dateFormatted: '12 jun, 11:30', dateTimestamp: nowTs + 14 * 86400, status: 'confirmed', isOperation: false, priceFormatted: '', receiptUrl: null },
            { id: 'demo-4', treatment: 'Bröstförstoring', city: 'Stockholm', provider: 'Marcus Ehrström Plastikkirurg', dateFormatted: '20 jun, 08:00', dateTimestamp: nowTs + 21 * 86400, status: 'confirmed', isOperation: true, priceFormatted: '', receiptUrl: null },
            { id: 'demo-5', treatment: 'Fillers', city: 'Skövde', provider: 'Silvia Galaz Injektionssjuksköterska', dateFormatted: '25 jun, 14:00', dateTimestamp: nowTs + 30 * 86400, status: 'confirmed', isOperation: false, priceFormatted: '', receiptUrl: null },
            { id: 'demo-6', treatment: 'Brazilian Butt Lift', city: 'Stockholm', provider: 'Patrik Höijer Plastikkirurg', dateFormatted: '3 jul, 07:30', dateTimestamp: nowTs + 40 * 86400, status: 'confirmed', isOperation: true, priceFormatted: '', receiptUrl: null },
          ];
          self.pastBookings = [
            { id: 'demo-p1', treatment: 'Konsultation Bröstförstoring', city: 'Stockholm', provider: 'Patrik Höijer Plastikkirurg', dateFormatted: '30 jan, 2026', status: 'completed', isOperation: false, priceFormatted: '', receiptUrl: null },
            { id: 'demo-p2', treatment: 'Konsultation Bröstreduktion', city: 'Stockholm', provider: 'Said Saidian Plastikkirurg', dateFormatted: '27 feb, 2026', status: 'cancelled', isOperation: false, priceFormatted: '', receiptUrl: null },
            { id: 'demo-p3', treatment: 'Konsultation Bröstförstoring och lyft', city: 'Stockholm', provider: 'Martin Halle Plastikkirurg', dateFormatted: '2 mar, 2026', status: 'completed', isOperation: false, priceFormatted: '', receiptUrl: null },
            { id: 'demo-p4', treatment: 'Bröstförstoring', city: 'Göteborg', provider: 'Mattias Lidén Plastikkirurg', dateFormatted: '15 mar, 2026', status: 'completed', isOperation: true, priceFormatted: '45 000 kr', receiptUrl: null },
            { id: 'demo-p5', treatment: 'Hudvårdsbehandling', city: 'Stockholm', provider: 'Anna Johansson Hudterapeut', dateFormatted: '20 mar, 2026', status: 'completed', isOperation: false, priceFormatted: '1 500 kr', receiptUrl: null },
            { id: 'demo-p6', treatment: 'Fillers', city: 'Skövde', provider: 'Silvia Galaz Injektionssjuksköterska', dateFormatted: '7 maj, 2026', status: 'cancelled', isOperation: false, priceFormatted: '', receiptUrl: null },
            { id: 'demo-p7', treatment: 'Konsultation Lipödem', city: 'Stockholm', provider: 'Said Saidian Plastikkirurg', dateFormatted: '13 apr, 2026', status: 'completed', isOperation: false, priceFormatted: '', receiptUrl: null },
            { id: 'demo-p8', treatment: 'Fettsugning', city: 'Stockholm', provider: 'Marcus Ehrström Plastikkirurg', dateFormatted: '28 apr, 2026', status: 'completed', isOperation: true, priceFormatted: '35 000 kr', receiptUrl: null },
          ];
          self.loading = false;
          return;
        }

        axios
          .get('/wp-json/nk/patient/getBookings/')
          .then((response) => {
            const data = response.data.data || response.data;
            if (data.upcoming && data.upcoming.length) {
              self.upcomingBookings = data.upcoming;
            }
            if (data.past && data.past.length) {
              self.pastBookings = data.past;
            }
            if (self.upcomingBookings.length > 0 && self.activeTab !== 'bokningar') {
              self.activeTab = 'bokningar';
            }
          })
          .catch((error) => {
            console.error('Failed to load bookings:', error);
          })
          .finally(() => {
            self.loading = false;
          });
      },

      logout() {
        axios
          .post('/wp-json/nk/patient/logout/')
          .then(() => {
            window.location.href = homeUrl('/');
          })
          .catch(() => {
            window.location.href = homeUrl('/');
          });
      },

      saveEmail() {
        if (!this.contactEmail) return;
        localStorage.setItem('nk_contact_email', this.contactEmail);
        this.emailSaved = true;
        this.fetchConversations();
      },

      saveEditedEmail() {
        if (!this.contactEmail) return;
        localStorage.setItem('nk_contact_email', this.contactEmail);
        this.editingEmail = false;
      },

      fetchConversationTypes() {
        var self = this;
        axios
          .get('/wp-json/nk/patient/getConversationTypes/')
          .then(function (response) {
            var data = response.data.data || response.data;
            var groups = data.groups || data;
            if (Array.isArray(groups) && groups.length) {
              self.typeGroups = groups.map(function (g) {
                return {
                  id: g.id,
                  key: g.key,
                  label: g.label,
                  types: (g.types || []).map(function (t) {
                    return { id: t.id, key: t.key, label: t.label };
                  }),
                };
              });
            }
          })
          .catch(function (error) {
            console.error('Failed to load conversation types:', error);
          });
      },

      fetchAutoReply() {
        var self = this;
        axios
          .get('/wp-json/nk/patient/getAutoReply/')
          .then(function (response) {
            var data = response.data.data || response.data;
            if (data && data.autoReply) {
              self.autoReplyText = DOMPurify.sanitize(data.autoReply);
            }
          })
          .catch(function (error) {
            console.error('Failed to load auto-reply:', error);
          });
      },

      fetchCbPatientId() {
        var self = this;
        axios
          .get('/wp-json/nk/patient/getCbPatientId/')
          .then(function (response) {
            var data = response.data.data || response.data;
            if (data && data.cbPatientId) {
              self.cbPatientId = data.cbPatientId;
              localStorage.setItem('nk_cb_patient_id', data.cbPatientId);
            } else {
              self.cbPatientId = '';
              localStorage.removeItem('nk_cb_patient_id');
            }
          })
          .catch(function () {});
      },

      buildConversationFingerprint(rawConversations) {
        var convs = Array.isArray(rawConversations) ? rawConversations : (rawConversations.conversations || []);
        return convs.map(function (c) {
          var msgs = c.messages || [];
          var msgIds = msgs.map(function (m) { return m.id || m._id || ''; }).join(',');
          return (c.id || c._id) + ':' + (c.status || '') + ':' + msgs.length + ':' + msgIds;
        }).join('|');
      },

      fetchConversations() {
        var self = this;
        var currentActive = this.activeConversation;
        return axios
          .get('/wp-json/nk/patient/getConversations/')
          .then((response) => {
            var data = response.data.data || response.data;
            var fingerprint = self.buildConversationFingerprint(data);
            if (fingerprint === self._lastConversationsFingerprint) {
              return;
            }
            self._lastConversationsFingerprint = fingerprint;

            var mapped = [];
            if (Array.isArray(data)) {
              mapped = self.mapConversations(data);
            } else if (data.conversations) {
              mapped = self.mapConversations(data.conversations);
            }
            self.conversations = mapped;
            try { localStorage.setItem('nk_seen_message_counts', JSON.stringify(self.seenMessageCounts)); } catch (e) {}
            if (self.activeTab === 'arenden') {
              self.openLatestConversation();
            }
            if (currentActive) {
              var still = mapped.find(function (c) { return c.id === currentActive; });
              if (still) {
                self.activeConversation = currentActive;
                if (still.unreadCount > 0) {
                  self.newMessageInThread = true;
                }
              }
            }
          })
          .catch((error) => {
            console.error('Failed to load conversations:', error);
          });
      },

      mapConversations(apiConversations) {
        var self = this;
        return apiConversations.map(function (conv) {
          var messages = (conv.messages || []).map(function (msg) {
            var isOwn = msg.sender === 'patient' || msg.senderType === 'patient';
            var senderName = isOwn ? 'Du' : (msg.senderName || 'NK');
            var initials = isOwn ? 'Du' : self.getInitials(senderName);
            var body = msg.message || msg.body || msg.text || msg.content || '';
            var rawDate = msg.createdAt || msg.timestamp || '';

            var contentBlocks = msg.content_blocks || msg.contentBlocks || [];
            var contentHtml = msg.contentHtml || msg.content_html || '';

            var hasInlineImages = contentBlocks.some(function (b) { return b.type === 'image'; });
            var renderedBody = '';
            if (hasInlineImages && contentBlocks.length) {
              renderedBody = contentBlocks.map(function (block) {
                if (block.type === 'image') {
                  var safeSrc = DOMPurify.sanitize(block.src);
                  return '<img src="' + safeSrc + '" class="ms-inline-image" />';
                }
                return DOMPurify.sanitize(linkify((block.content || '').replace(/\n/g, '<br>')), PURIFY_OPTS);
              }).join('');
            } else if (contentHtml) {
              renderedBody = DOMPurify.sanitize(linkify(contentHtml), PURIFY_OPTS);
            } else {
              renderedBody = DOMPurify.sanitize(linkify(body.replace(/\n/g, '<br>')), PURIFY_OPTS);
            }

            var attachments = [];
            var imageUrls = msg.images || [];
            var imageNames = msg.attachmentNames || [];

            if (imageUrls.length) {
              attachments = imageUrls.map(function (url, idx) {
                return {
                  url: url,
                  name: imageNames[idx] || url.split('/').pop().split('?')[0] || 'Bilaga',
                };
              });
            } else {
              var rawAttachments = msg.attachment_url || msg.attachmentUrl || msg.attachments || [];
              if (typeof rawAttachments === 'string') {
                try { rawAttachments = JSON.parse(rawAttachments); } catch (e) { rawAttachments = []; }
              }
              if (Array.isArray(rawAttachments)) {
                attachments = rawAttachments.map(function (att) {
                  if (typeof att === 'string') {
                    return { url: att, name: att.split('/').pop().split('?')[0] || 'Bilaga' };
                  }
                  return {
                    url: att.url || att.src || att.key || '',
                    name: att.name || att.filename || 'Bilaga',
                  };
                });
              }
            }

            return {
              id: msg.id || msg._id || ('msg-' + Math.random()),
              sender: senderName,
              initials: initials,
              senderImageUrl: msg.senderImageUrl || '',
              rawDate: rawDate,
              date: rawDate ? self.formatDateTime(new Date(rawDate)) : '',
              body: body,
              renderedBody: renderedBody,
              attachments: attachments,
              isOwn: isOwn,
            };
          });

          var convId = conv.id || conv._id;
          var totalMessages = messages.length;
          var seenCount = self.seenMessageCounts[convId];
          var unread;
          if (seenCount !== undefined) {
            unread = Math.max(0, totalMessages - seenCount);
          } else {
            unread = 0;
            self.seenMessageCounts[convId] = totalMessages;
          }

          var sortedMessages = messages.slice().sort(function (a, b) {
            var da = a.rawDate ? new Date(a.rawDate).getTime() : 0;
            var db = b.rawDate ? new Date(b.rawDate).getTime() : 0;
            return db - da;
          });


          return {
            id: convId,
            subject: conv.subject || 'Ärende',
            type: conv.tag || '',
            status: conv.status || 'open',
            startDate: conv.createdAt ? self.formatDate(new Date(conv.createdAt)) : '',
            unreadCount: unread,
            messages: sortedMessages,
          };
        });
      },

      getInitials(name) {
        if (!name) return '?';
        var parts = name.split(' ');
        if (parts.length >= 2) {
          return parts[0][0].toUpperCase() + parts[parts.length - 1][0].toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
      },

      handleFileSelect(e) {
        var self = this;
        var MAX_FILE_SIZE = 20 * 1024 * 1024;
        var files = Array.from(e.target.files);
        self.uploadErrorMessage = '';
        var tooLarge = files.filter(function (f) { return f.size > MAX_FILE_SIZE; });
        if (tooLarge.length) {
          var names = tooLarge.map(function (f) { return f.name; }).join(', ');
          self.uploadErrorMessage = 'Följande filer är för stora (max 20 MB): ' + names;
          files = files.filter(function (f) { return f.size <= MAX_FILE_SIZE; });
        }
        files.forEach(function (f) { self.attachedFiles.push(f); });
        e.target.value = '';
      },

      handleReplyFileSelect(e) {
        var self = this;
        var MAX_FILE_SIZE = 20 * 1024 * 1024;
        var files = Array.from(e.target.files);
        self.uploadErrorMessage = '';
        var tooLarge = files.filter(function (f) { return f.size > MAX_FILE_SIZE; });
        if (tooLarge.length) {
          var names = tooLarge.map(function (f) { return f.name; }).join(', ');
          self.uploadErrorMessage = 'Följande filer är för stora (max 20 MB): ' + names;
          files = files.filter(function (f) { return f.size <= MAX_FILE_SIZE; });
        }
        for (var i = 0; i < files.length; i++) {
          this.replyFiles.push(files[i]);
        }
        e.target.value = '';
      },

      removeFile(index) {
        this.attachedFiles.splice(index, 1);
      },

      compressImage(file, maxWidth, quality) {
        maxWidth = maxWidth || 1920;
        quality = quality || 0.82;
        return new Promise(function (resolve, reject) {
          if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/i)) {
            resolve(file);
            return;
          }
          var reader = new FileReader();
          reader.onerror = function () { resolve(file); };
          reader.onload = function () {
            var img = new Image();
            img.onerror = function () { resolve(file); };
            img.onload = function () {
              if (img.width <= maxWidth && file.size < 1024 * 1024) {
                resolve(file);
                return;
              }
              var scale = Math.min(1, maxWidth / img.width);
              var w = Math.round(img.width * scale);
              var h = Math.round(img.height * scale);
              var canvas = document.createElement('canvas');
              canvas.width = w;
              canvas.height = h;
              var ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, w, h);
              canvas.toBlob(function (blob) {
                if (!blob || blob.size >= file.size) {
                  resolve(file);
                  return;
                }
                var ext = file.name.replace(/^.*\./, '').toLowerCase();
                var newName = file.name;
                if (ext === 'png' || ext === 'webp') {
                  newName = file.name.replace(/\.[^.]+$/, '.jpg');
                }
                var compressed = new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
                resolve(compressed);
              }, 'image/jpeg', quality);
            };
            img.src = reader.result;
          };
          reader.readAsDataURL(file);
        });
      },

      fileToBase64(file) {
        return new Promise(function (resolve, reject) {
          var reader = new FileReader();
          reader.onload = function () { resolve(reader.result); };
          reader.onerror = function () { reject(reader.error); };
          reader.readAsDataURL(file);
        });
      },

      filesToAttachments(files) {
        var self = this;
        return Promise.all(files.map(function (f) {
          return self.compressImage(f).then(function (processed) {
            return new Promise(function (resolve, reject) {
              var reader = new FileReader();
              reader.onload = function () {
                resolve({ data: reader.result, name: processed.name });
              };
              reader.onerror = function () { reject(reader.error); };
              reader.readAsDataURL(processed);
            });
          });
        }));
      },

      sendMessage() {
        this.uploadErrorMessage = '';
        if (!this.messageSubject || !this.messageBody || !this.typeFullySelected) return;
        if (this.hasAdminFields) {
          if (!this.adminTreatmentDesc || !this.adminDoctorName || !this.adminTreatmentDate) return;
          if (this.isAdminRequisition && !this.adminDelivery) return;
          if (this.isAdminRequisition && this.adminDelivery === 'post' && (!this.adminAddressName || !this.adminAddressStreet || !this.adminAddressZip || !this.adminAddressCity)) return;
        }

        var subject = this.messageSubject;
        var body = this.messageBody;
        var tag = this.resolvedTypeKey;
        var manualPid = this.manualPatientId;

        var adminFields = null;
        if (this.hasAdminFields) {
          adminFields = {};
          adminFields.treatmentDescription = this.adminTreatmentDesc || '';
          adminFields.doctor = this.adminDoctorName || '';
          adminFields.treatmentDate = this.adminTreatmentDate || '';
          adminFields.comment = this.adminComment || '';
          if (this.isAdminRequisition) {
            adminFields.requisitionAccepted = true;
          }
          if (this.isAdminRequisition && this.adminDelivery) {
            adminFields.deliveryOption = this.adminDelivery;
            if (this.adminDelivery === 'post') {
              adminFields.deliveryAddress = {
                name: this.adminAddressName,
                street: this.adminAddressStreet,
                zip: this.adminAddressZip,
                city: this.adminAddressCity,
              };
            }
          }
        }
        var files = this.attachedFiles.slice();
        var locationVal = this.selectedLocation;
        var self = this;
        var hasFiles = files.length > 0;

        this.messageSubject = '';
        this.messageType = '';
        this.messageSubType = '';
        this.adminTreatmentDesc = '';
        this.adminDoctorName = '';
        this.adminTreatmentDate = '';
        this.adminComment = '';
        this.adminDelivery = '';
        this.adminRequisitionAccepted = false;
        this.adminAddressName = '';
        this.adminAddressStreet = '';
        this.adminAddressZip = '';
        this.adminAddressCity = '';
        this.manualPatientId = '';
        this.messageBody = '';
        this.attachedFiles = [];
        this.selectedLocation = '';
        this.sendingMessage = true;
        this.messageSentSuccess = false;
        this.activeConversation = null;
        this.sendingUploadText = 'Ditt ärende skapas...';

        if (hasFiles) {
          var fileCount = files.length;
          this._sendingTimer = setTimeout(function () {
            self.sendingUploadText = fileCount > 1 ? 'Laddar upp filer...' : 'Laddar upp fil...';
          }, 2000);
        }

        var sendPayload = function (attachments) {
          var payload = {
            subject: subject,
            message: body,
            tag: tag,
            email: self.contactEmail,
          };
          if (attachments && attachments.length) {
            payload.attachments = attachments;
          }
          if (locationVal) {
            payload.location = locationVal;
          }
          if (self.autoReplyText) {
            payload.autoReplyText = self.autoReplyText;
          }
          if (adminFields) {
            payload.adminFields = adminFields;
          }
          if (self.cbPatientId) {
            payload.cbPatientId = self.cbPatientId;
          } else if (manualPid) {
            payload.cbPatientId = manualPid;
          }
          return axios.post('/wp-json/nk/patient/createConversation/', payload);
        };

        var sendPromise = files.length
          ? self.filesToAttachments(files).then(sendPayload)
          : sendPayload([]);

        sendPromise
          .then(function (response) {
            var data = response.data.data || response.data;
            var realId = data ? (data.id || data.conversationId || data._id) : null;

            self._lastConversationsFingerprint = null;
            axios.get('/wp-json/nk/patient/getConversations/').then(function (res) {
              var convData = res.data.data || res.data;
              self._lastConversationsFingerprint = self.buildConversationFingerprint(convData);
              var mapped = [];
              if (Array.isArray(convData)) {
                mapped = self.mapConversations(convData);
              } else if (convData.conversations) {
                mapped = self.mapConversations(convData.conversations);
              }
              var targetId = realId;
              if (targetId) {
                var newConv = mapped.find(function (c) { return c.id === targetId; });
                if (newConv) {
                  self.seenMessageCounts[targetId] = newConv.messages.length;
                  localStorage.setItem('nk_seen_message_counts', JSON.stringify(self.seenMessageCounts));
                  newConv.unreadCount = 0;
                }
              }
              self.conversations = mapped;
              clearTimeout(self._sendingTimer);
              self.sendingMessage = false;
              self.messageSentSuccess = false;
              self.showNewContactForm = false;
              self.activeTab = 'arenden';
              if (targetId) {
                self.activeConversation = targetId;
                var targetConv = mapped.find(function (c) { return c.id === targetId; });
                if (targetConv) {
                  targetConv.messages.forEach(function (m) {
                    self.$set(self.expandedMessages, m.id, true);
                  });
                }
              } else if (mapped.length) {
                self.activeConversation = mapped[0].id;
              }
              if (self.autoReplyText) {
                self.showAutoReplyPopup = true;
              }
            });
          })
          .catch(function (error) {
            console.error('Failed to create conversation:', error);
            clearTimeout(self._sendingTimer);
            self.sendingMessage = false;
            self.showNewContactForm = true;
            var resp = error.response && error.response.data;
            var detail = resp && resp.data && resp.data.details ? resp.data.details : (resp && resp.data ? resp.data : resp);
            if (detail && detail.error === 'RATE_LIMIT') {
              self.rateLimitMessage = detail.message || 'Du kan inte skapa en ny konversation just nu. Försök igen om en stund.';
              if (self.rateLimitTimer) clearTimeout(self.rateLimitTimer);
              var secs = detail.retryAfterSeconds || 300;
              self.rateLimitTimer = setTimeout(function () {
                self.rateLimitMessage = '';
              }, secs * 1000);
            } else {
              self.uploadErrorMessage = 'Något gick fel vid skapande av ärende. Försök igen eller kontakta oss på 08-411 76 76.';
            }
          });
      },

      viewNewMessages() {
        this.newMessageInThread = false;
        var conv = this.activeConversationData();
        if (conv) {
          var self = this;
          var unreadCount = conv.unreadCount || 0;
          if (unreadCount > 0) {
            conv.messages.slice(0, unreadCount).forEach(function (msg) {
              self.$set(self.expandedMessages, msg.id, true);
            });
          }
          conv.unreadCount = 0;
          this.$set(this.seenMessageCounts, conv.id, conv.messages.length);
          try { localStorage.setItem('nk_seen_message_counts', JSON.stringify(this.seenMessageCounts)); } catch (e) {}
        }
      },

      // Opens the conversation with the newest message, once per page load, so the
      // Ärenden tab never lands on an empty thread. Once only: polling must not reopen
      // a thread after the patient went back to the list on mobile.
      openLatestConversation() {
        if (this.autoOpenedLatest || this.activeConversation || !this.conversations.length) return;
        var newest = function (c) {
          var d = c.messages.length && c.messages[0].rawDate;
          return d ? new Date(d).getTime() : 0;
        };
        var latest = this.conversations.reduce(function (best, c) {
          return newest(c) > newest(best) ? c : best;
        }, this.conversations[0]);
        this.autoOpenedLatest = true;
        this.selectConversation(latest.id);
      },

      selectConversation(convId) {
        this.activeConversation = convId;
        this.newMessageInThread = false;
        this.showNewContactForm = false;
        this.showReplyForm = false;
        this.replyBody = '';
        this.replyFiles = [];
        this.messageSentSuccess = false;
        var conv = this.conversations.find(function (c) { return c.id === convId; });
        if (conv) {
          var self = this;
          if (conv.messages.length > 0) {
            self.$set(self.expandedMessages, conv.messages[0].id, true);
          }
          var unreadCount = conv.unreadCount || 0;
          if (unreadCount > 0) {
            conv.messages.slice(0, unreadCount).forEach(function (msg) {
              self.$set(self.expandedMessages, msg.id, true);
            });
          }
          conv.unreadCount = 0;
          this.$set(this.seenMessageCounts, convId, conv.messages.length);
          try { localStorage.setItem('nk_seen_message_counts', JSON.stringify(this.seenMessageCounts)); } catch (e) {}
        }
      },

      startNewContact() {
        this.activeConversation = null;
        this.showNewContactForm = true;
        this.showReplyForm = false;
        this.replyBody = '';
        this.messageSentSuccess = false;
        this.sendingMessage = false;
        this.selectedLocation = '';
        this.messageType = '';
        this.messageSubType = '';
        this.typeDropdownOpen = false;
        this.subTypeDropdownOpen = false;
        this.locationDropdownOpen = false;
        this.adminTreatmentDesc = '';
        this.adminDoctorName = '';
        this.adminTreatmentDate = '';
        this.adminComment = '';
        this.adminDelivery = '';
        this.adminRequisitionAccepted = false;
      },

      toggleTypeDropdown() {
        if (!this.selectedLocation) return;
        this.typeDropdownOpen = !this.typeDropdownOpen;
      },

      closeTypeDropdown() {
        this.typeDropdownOpen = false;
      },

      closeLocationDropdown() {
        this.locationDropdownOpen = false;
      },

      selectGroup(groupKey) {
        this.messageType = groupKey;
        this.messageSubType = '';
        this.typeDropdownOpen = false;
        this.subTypeDropdownOpen = false;
        this.adminTreatmentDesc = '';
        this.adminDoctorName = '';
        this.adminTreatmentDate = '';
        this.adminComment = '';
        this.adminDelivery = '';
        this.adminRequisitionAccepted = false;
      },

      selectSubType(subKey) {
        this.messageSubType = subKey;
        this.subTypeDropdownOpen = false;
        this.adminTreatmentDesc = '';
        this.adminDoctorName = '';
        this.adminTreatmentDate = '';
        this.adminComment = '';
        this.adminDelivery = '';
        this.adminRequisitionAccepted = false;
      },

      closeSubTypeDropdown() {
        this.subTypeDropdownOpen = false;
      },

      openReplyForm() {
        this.showReplyForm = true;
        this.replyFiles = [];
        this.$nextTick(function () {
          var el = document.querySelector('.ms-reply-textarea');
          if (el) el.focus();
        });
      },

      removeReplyFile(index) {
        this.replyFiles.splice(index, 1);
      },

      replyToConversation() {
        this.uploadErrorMessage = '';
        if (!this.replyBody.trim() || !this.activeConversation) return;

        var convId = this.activeConversation;
        var body = this.replyBody;
        var fileNames = this.replyFiles.map(function (f) { return f.name; });
        var self = this;

        var conv = this.activeConversationData();
        if (conv) {
          conv.messages.unshift({
            id: 'temp-reply-' + Date.now(),
            sender: 'Du',
            initials: 'Du',
            rawDate: new Date().toISOString(),
            date: this.formatDateTime(new Date()),
            body: body,
            attachments: fileNames,
            isOwn: true,
          });
          conv.unreadCount = 0;
          this.seenMessageCounts[convId] = conv.messages.length;
          localStorage.setItem('nk_seen_message_counts', JSON.stringify(this.seenMessageCounts));
        }

        var files = this.replyFiles.slice();
        this.replyBody = '';
        this.replyFiles = [];
        this.showReplyForm = false;

        var convData = conv;
        var sendReply = function (attachments) {
          var payload = {
            conversationId: convId,
            message: body,
            email: self.contactEmail,
            subject: convData ? convData.subject : '',
            tag: convData ? convData.type : '',
          };
          if (attachments && attachments.length) {
            payload.attachments = attachments;
          }
          if (self.cbPatientId) {
            payload.cbPatientId = self.cbPatientId;
          }
          return axios.post('/wp-json/nk/patient/replyConversation/', payload);
        };

        self.sendingReply = true;

        var replyPromise = files.length
          ? self.filesToAttachments(files).then(sendReply)
          : sendReply([]);

        replyPromise
          .then(function () {
            self._lastConversationsFingerprint = null;
            return axios.get('/wp-json/nk/patient/getConversations/');
          })
          .then(function (res) {
            var freshData = res.data.data || res.data;
            self._lastConversationsFingerprint = self.buildConversationFingerprint(freshData);
            var mapped = [];
            if (Array.isArray(freshData)) {
              mapped = self.mapConversations(freshData);
            } else if (freshData.conversations) {
              mapped = self.mapConversations(freshData.conversations);
            }
            var updatedConv = mapped.find(function (c) { return c.id === convId; });
            if (updatedConv) {
              self.seenMessageCounts[convId] = updatedConv.messages.length;
              localStorage.setItem('nk_seen_message_counts', JSON.stringify(self.seenMessageCounts));
              updatedConv.unreadCount = 0;
            }
            self.conversations = mapped;
            self.sendingReply = false;
          })
          .catch(function (error) {
            console.error('Failed to send reply:', error);
            self.sendingReply = false;
            var resp = error.response && error.response.data;
            var detail = resp && resp.data && resp.data.details ? resp.data.details : (resp && resp.data ? resp.data : resp);
            if (detail && detail.error === 'RATE_LIMIT') {
              self.rateLimitMessage = detail.message || 'Du kan inte skicka meddelanden just nu. Försök igen om en stund.';
              if (self.rateLimitTimer) clearTimeout(self.rateLimitTimer);
              var secs = detail.retryAfterSeconds || 300;
              self.rateLimitTimer = setTimeout(function () {
                self.rateLimitMessage = '';
              }, secs * 1000);
            } else {
              self.uploadErrorMessage = 'Något gick fel vid sändning. Försök igen eller kontakta oss på 08-411 76 76.';
            }
          });
      },

      toggleMessage(msgId) {
        this.$set(this.expandedMessages, msgId, !this.isMessageExpanded(msgId));
      },

      // Messages are expanded by default; only an explicit collapse (false) hides the body.
      // Patients didn't notice the expand arrow and thought older messages were gone.
      isMessageExpanded(msgId) {
        return this.expandedMessages[msgId] !== false;
      },

      activeConversationData() {
        var self = this;
        return this.conversations.find(function (c) { return c.id === self.activeConversation; }) || null;
      },

      formatDate(d) {
        var months = ['jan', 'feb', 'mar', 'apr', 'maj', 'juni', 'juli', 'aug', 'sep', 'okt', 'nov', 'dec'];
        return d.getDate() + ' ' + months[d.getMonth()] + ', ' + d.getFullYear();
      },

      formatDateTime(d) {
        var months = ['jan', 'feb', 'mar', 'apr', 'maj', 'juni', 'juli', 'aug', 'sep', 'okt', 'nov', 'dec'];
        var hh = String(d.getHours()).padStart(2, '0');
        var mm = String(d.getMinutes()).padStart(2, '0');
        return d.getDate() + ' ' + months[d.getMonth()] + ', ' + d.getFullYear() + ' - ' + hh + ':' + mm;
      },

      startPolling() {
        this.stopPolling();
        var self = this;
        this.conversationPollTimer = setInterval(function () {
          self.fetchConversations();
        }, 3000);
      },

      stopPolling() {
        if (this.conversationPollTimer) {
          clearInterval(this.conversationPollTimer);
          this.conversationPollTimer = null;
        }
      },

      rebookBooking(booking) {
        if (booking.rebookUrl) {
          window.location.href = booking.rebookUrl;
        } else {
          window.location.href = bookingUrl();
        }
      },

      canCancelBooking(booking) {
        if (!booking.dateTimestamp) return true;
        var nowSec = Math.floor(Date.now() / 1000);
        return (booking.dateTimestamp - nowSec) >= 86400;
      },

      cancelBooking(booking) {
        this.cancelTarget = booking;
        this.showCancelModal = true;
      },

      dismissCancelModal() {
        this.showCancelModal = false;
        this.cancelTarget = null;
      },

      confirmCancel() {
        var booking = this.cancelTarget;
        if (!booking) return;
        var self = this;
        self.showCancelModal = false;
        self.cancellingBookingId = booking.id;
        axios.post('/wp-json/nk/patient/cancelBooking/', { booking_id: booking.id })
          .then(function () {
            var idx = self.upcomingBookings.findIndex(function (b) { return b.id === booking.id; });
            if (idx !== -1) {
              var cancelled = self.upcomingBookings.splice(idx, 1)[0];
              cancelled.status = 'cancelled';
              self.pastBookings.unshift(cancelled);
            }
            self.cancellingBookingId = null;
            self.cancelTarget = null;
          })
          .catch(function () {
            self.cancellingBookingId = null;
            self.cancelTarget = null;
            self.showCancelModal = true;
          });
      },
    },
  });
});
